import React, { useEffect, useMemo, useState } from 'react';
import { Icon } from '@iconify/react';
import { Link } from 'react-router-dom';

function headers() {
  const token = sessionStorage.getItem('syrakis_token');
  return { Accept: 'application/json', 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

function csvValue(value) {
  const text = value === null || value === undefined ? '' : String(value);
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

const REPORT_COLUMNS = ['origen', 'id', 'nombre', 'descripcion', 'discovery_state', 'validation_state', 'migration_state', 'installation_state', 'tenant_state', 'activation_ready', 'activation_blockers', 'manifest_valid', 'manifest_path', 'migrations', 'validation_errors'];

function downloadCsv(rows, filename) {
  const content = [REPORT_COLUMNS.join(','), ...rows.map((row) => REPORT_COLUMNS.map((column) => csvValue(row[column])).join(','))].join('\n');
  const blob = new Blob([`\uFEFF${content}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

async function copyTable(rows) {
  const content = [REPORT_COLUMNS.join('\t'), ...rows.map((row) => REPORT_COLUMNS.map((column) => String(row[column] ?? '').replace(/[\t\r\n]+/g, ' ')).join('\t'))].join('\n');
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(content);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = content;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();
  const copied = document.execCommand('copy');
  textarea.remove();
  if (!copied) throw new Error('El navegador no permitió copiar la tabla.');
}

function statusBadge(value, expected, labels = {}) {
  const label = labels[value] || value || 'pendiente';
  const good = value === expected;
  return <span className={`badge me-1 ${good ? 'bg-success-50 text-success-600' : 'bg-warning-50 text-warning-700'}`}>{label}</span>;
}

function isCoreFeature(feature) {
  return Boolean(Number(feature.mandatory_core)) || feature.feature_kind === 'core';
}

function orderByDependencies(features) {
  const byId = new Map(features.map((feature) => [feature.id, feature]));
  const dependencies = new Map();
  const dependents = new Map(features.map((feature) => [feature.id, []]));

  for (const feature of features) {
    const direct = [...new Set((feature.dependencies || [])
      .map((dependency) => typeof dependency === 'string' ? dependency : dependency?.feature || dependency?.id)
      .filter((dependencyId) => dependencyId && dependencyId !== feature.id && byId.has(dependencyId)))];
    dependencies.set(feature.id, direct);
    for (const dependencyId of direct) dependents.get(dependencyId).push(feature.id);
  }

  const compare = (leftId, rightId) => {
    const left = byId.get(leftId);
    const right = byId.get(rightId);
    return String(left.nombre || left.id).localeCompare(String(right.nombre || right.id), 'es')
      || String(left.id).localeCompare(String(right.id));
  };
  const remaining = new Map([...dependencies].map(([id, items]) => [id, items.length]));
  const ready = [...remaining].filter(([, count]) => count === 0).map(([id]) => id).sort(compare);
  const ordered = [];

  while (ready.length) {
    const id = ready.shift();
    ordered.push(byId.get(id));
    for (const dependentId of dependents.get(id) || []) {
      const count = remaining.get(dependentId) - 1;
      remaining.set(dependentId, count);
      if (count === 0) {
        ready.push(dependentId);
        ready.sort(compare);
      }
    }
  }

  // Si hay un ciclo en los manifiestos, conserva esas features al final de su
  // grupo en orden estable; la validación del catálogo las marcará bloqueadas.
  const emitted = new Set(ordered.map((feature) => feature.id));
  return ordered.concat(features.filter((feature) => !emitted.has(feature.id)).sort((a, b) => compare(a.id, b.id)));
}

export default function FeatureCatalogPage() {
  const [features, setFeatures] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [discoveryError, setDiscoveryError] = useState('');
  const [message, setMessage] = useState('');

  async function load() {
    try {
      const response = await fetch('/api/features', { credentials: 'same-origin', headers: headers() });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error);
      setFeatures(data.features || []);
    } catch (reason) {
      setError(reason.message || 'No se pudo cargar el catálogo.');
    }
  }

  async function loadDiscovery() {
    setDiscoveryError('');
    try {
      const response = await fetch('/api/features/discovery', { credentials: 'same-origin', headers: headers() });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || data.error);
      setCandidates(data.features || []);
    } catch (reason) {
      setCandidates([]);
      setDiscoveryError(reason.message || 'No se pudo explorar el filesystem.');
    }
  }

  useEffect(() => { load(); loadDiscovery(); }, []);

  async function discoveryAction(feature, action) {
    setBusy(`discover:${feature.id}`);
    setError('');
    setMessage('');
    try {
      const response = await fetch(`/api/features/${encodeURIComponent(feature.id)}/${action}`, { method: 'POST', credentials: 'same-origin', headers: headers() });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || data.error);
      setMessage(data.message || `Acción ${action} completada.`);
      await load();
      await loadDiscovery();
    } catch (reason) {
      setError(reason.message || 'No se pudo ejecutar la operación.');
    } finally {
      setBusy('');
    }
  }

  async function toggle(feature) {
    setBusy(feature.id);
    setError('');
    setMessage('');
    try {
      const response = await fetch(`/api/features/${encodeURIComponent(feature.id)}`, { method: 'PUT', credentials: 'same-origin', headers: headers(), body: JSON.stringify({ enabled: !Boolean(feature.tenant_enabled) }) });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || data.error);
      setMessage(data.message);
      await load();
    } catch (reason) {
      setError(reason.message || 'No se pudo actualizar la feature.');
    } finally {
      setBusy('');
    }
  }

  async function saveDescription(event) {
    event.preventDefault();
    setBusy(editing.id);
    setError('');
    setMessage('');
    try {
      const response = await fetch(`/api/features/${encodeURIComponent(editing.id)}`, { method: 'PATCH', credentials: 'same-origin', headers: headers(), body: JSON.stringify({ nombre: editing.nombre, descripcion: editing.descripcion }) });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || data.error);
      setMessage(data.message);
      setEditing(null);
      await load();
    } catch (reason) {
      setError(reason.message || 'No se pudo editar la feature.');
    } finally {
      setBusy('');
    }
  }

  async function copyReport() {
    setError('');
    try {
      await copyTable(exportRows);
      setMessage('Tabla copiada al portapapeles. Puedes pegarla aquí para revisar el estado.');
    } catch (reason) {
      setError(reason.message || 'No se pudo copiar la tabla.');
    }
  }

  const filteredFeatures = useMemo(() => {
    const value = search.trim().toLowerCase();
    const filtered = features.filter((feature) => {
      const matchesStatus = statusFilter === 'all'
        || (statusFilter === 'enabled' && Boolean(feature.tenant_enabled))
        || (statusFilter === 'disabled' && !Boolean(feature.tenant_enabled))
        || (statusFilter === 'blocked' && !Boolean(feature.activation_ready));
      const matchesSearch = !value || [feature.id, feature.nombre, feature.descripcion].some((field) => String(field || '').toLowerCase().includes(value));
      return matchesStatus && matchesSearch;
    });
    const businessFeatures = orderByDependencies(filtered.filter((feature) => !isCoreFeature(feature)));
    const coreFeatures = orderByDependencies(filtered.filter(isCoreFeature));
    return [...businessFeatures, ...coreFeatures];
  }, [features, search, statusFilter]);

  const ready = (feature) => Boolean(feature.activation_ready);
  const blockerLabel = (feature) => (feature.activation_blockers || []).join(', ') || 'La feature aún no está lista para habilitarse.';
  const installedIds = new Set(features.map((feature) => feature.id));
  const unregisteredCandidates = candidates.filter((candidate) => !installedIds.has(candidate.id));
  const exportRows = [
    ...features.map((feature) => ({ ...feature, origen: 'catalogo', manifest_path: feature.seed_manifest || '', migrations: '', validation_errors: (feature.activation_blockers || []).join('; ') })),
    ...unregisteredCandidates.map((candidate) => ({
      origen: 'filesystem', id: candidate.id, nombre: candidate.manifest?.manifest?.name || candidate.id,
      descripcion: candidate.manifest?.manifest?.description || '', discovery_state: candidate.discovered ? 'discovered' : 'pending',
      validation_state: candidate.valid ? 'validated' : 'rejected', migration_state: 'pending', installation_state: 'pending',
      tenant_state: 'disabled', activation_ready: candidate.valid ? '1' : '0', activation_blockers: candidate.errors?.join('; ') || '',
      manifest_valid: candidate.manifest?.valid ? '1' : '0', manifest_path: candidate.manifest?.path || '', migrations: candidate.migrations?.length || 0,
      validation_errors: candidate.errors?.join('; ') || '',
    })),
  ];

  return (
    <div className="dashboard-main-body">
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-24">
        <div><h4 className="mb-8">Catálogo de features</h4><p className="text-secondary-light mb-0">Descubre, valida e instala plugins; luego habilítalos por tenant.</p></div>
        <div className="d-flex flex-wrap gap-2 align-items-center"><button className="btn btn-outline-primary" onClick={loadDiscovery}><Icon icon="solar:refresh-linear" className="me-1" />Explorar filesystem</button><button className="btn btn-outline-info" disabled={!exportRows.length} onClick={copyReport}><Icon icon="solar:copy-linear" className="me-1" />Copiar tabla</button><button className="btn btn-outline-success" disabled={!exportRows.length} onClick={() => downloadCsv(exportRows, `reporte-features-${new Date().toISOString().slice(0, 10)}.csv`)}><Icon icon="solar:download-linear" className="me-1" />Exportar reporte CSV</button><span className="badge bg-warning-50 text-warning-700"><Icon icon="solar:settings-linear" className="me-1" />Solo superadmin</span></div>
      </div>
      {error && <div className="alert alert-danger mb-20">{error}</div>}
      {message && <div className="alert alert-success mb-20">{message}</div>}
      {discoveryError && <div className="alert alert-warning mb-20"><strong>Descubrimiento no disponible:</strong> {discoveryError}. El catálogo registrado sí puede exportarse.</div>}

      <div className="row g-3 mb-20"><div className="col-md-4"><button type="button" className={`card h-100 w-100 text-start border-0 ${statusFilter === 'all' ? 'shadow-sm' : ''}`} onClick={() => setStatusFilter('all')}><div className="card-body"><small className="text-secondary-light">Registradas</small><h4 className="mb-0">{features.length}</h4></div></button></div><div className="col-md-4"><button type="button" className={`card h-100 w-100 text-start border-0 ${statusFilter === 'enabled' ? 'shadow-sm border-success' : ''}`} onClick={() => setStatusFilter('enabled')}><div className="card-body"><small className="text-secondary-light">Habilitadas en tenant</small><h4 className="mb-0 text-success">{features.filter((feature) => feature.tenant_enabled).length}</h4></div></button></div><div className="col-md-4"><button type="button" className={`card h-100 w-100 text-start border-0 ${statusFilter === 'disabled' ? 'shadow-sm border-danger' : ''}`} onClick={() => setStatusFilter('disabled')}><div className="card-body"><small className="text-secondary-light">Deshabilitadas</small><h4 className="mb-0 text-danger">{features.filter((feature) => !feature.tenant_enabled).length}</h4></div></button></div></div>

      {unregisteredCandidates.length > 0 && <div className="card mb-20"><div className="card-header d-flex justify-content-between align-items-center"><div><h6 className="mb-1">Features detectadas en filesystem</h6><small className="text-secondary-light">Las detectadas aún no necesariamente están instaladas.</small></div><span className="badge bg-info-50 text-info-600">{unregisteredCandidates.length} detectadas</span></div><div className="table-responsive"><table className="table bordered-table mb-0 align-middle"><thead><tr><th>Feature</th><th>Manifiesto</th><th>Migraciones</th><th>Estado</th><th>Catálogo</th><th className="text-end">Acciones</th></tr></thead><tbody>{unregisteredCandidates.map((candidate) => <tr key={candidate.id}><td className="fw-semibold">{candidate.id}</td><td><small>{candidate.manifest.path || 'No encontrado'}</small></td><td>{candidate.migrations.length}</td><td><span className={`badge ${candidate.valid ? 'bg-success-50 text-success-600' : 'bg-danger-50 text-danger-600'}`}>{candidate.valid ? 'Válida' : 'Bloqueada'}</span>{candidate.errors?.length > 0 && <div><small className="text-danger">{candidate.errors[0]}</small></div>}</td><td><span className="badge bg-secondary-100 text-secondary-600">No registrada</span></td><td className="text-end">{candidate.valid && <><button className="btn btn-sm btn-outline-secondary me-2" disabled={busy === `discover:${candidate.id}`} onClick={() => discoveryAction(candidate, 'discover')}>Registrar</button><button className="btn btn-sm btn-primary" disabled={busy === `discover:${candidate.id}`} onClick={() => discoveryAction(candidate, 'install')}>Instalar</button></>}</td></tr>)}</tbody></table></div></div>}

      {editing && <div className="card mb-20"><div className="card-body p-24"><h6 className="mb-20">Editar feature: {editing.id}</h6><form onSubmit={saveDescription}><div className="mb-16"><label className="form-label">Nombre visible</label><input className="form-control" value={editing.nombre} onChange={(event) => setEditing({ ...editing, nombre: event.target.value })} required /></div><div className="mb-20"><label className="form-label">Descripción</label><textarea className="form-control" rows="3" value={editing.descripcion || ''} onChange={(event) => setEditing({ ...editing, descripcion: event.target.value })} /></div><button className="btn btn-primary me-2" disabled={busy === editing.id}>{busy === editing.id ? 'Guardando...' : 'Guardar edición'}</button><button type="button" className="btn btn-light" onClick={() => setEditing(null)}>Cancelar</button></form></div></div>}

      <div className="card">
        <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-3">
          <div>
            <h6 className="mb-1">Features registradas</h6>
            <small className="text-secondary-light">Filtro activo: {statusFilter === 'all' ? 'todas' : statusFilter === 'enabled' ? 'habilitadas' : statusFilter === 'disabled' ? 'deshabilitadas' : 'bloqueadas técnicamente'}.</small>
          </div>
          <input className="form-control" style={{ maxWidth: 280 }} placeholder="Buscar por id o nombre" value={search} onChange={(event) => setSearch(event.target.value)} />
        </div>
        <div className="table-responsive">
          <table className="table bordered-table mb-0 align-middle">
            <thead><tr><th>Feature</th><th>Descripción</th><th>Ciclo global</th><th>Validación técnica</th><th>Tenant</th><th className="text-end">Acciones</th></tr></thead>
            <tbody>
              {filteredFeatures.map((feature, index) => {
                const core = isCoreFeature(feature);
                const previousCore = index > 0 && isCoreFeature(filteredFeatures[index - 1]);
                const startsGroup = index === 0 || core !== previousCore;
                return <React.Fragment key={feature.id}>
                  {startsGroup && <tr className={core ? 'table-primary' : 'table-light'}><th colSpan="6">{core ? 'Features Core · fundacionales primero' : 'Features de negocio · orden de dependencias'}</th></tr>}
                  <tr style={{
                    backgroundColor: core ? 'rgba(13, 110, 253, 0.075)' : !feature.tenant_enabled ? 'rgba(220, 53, 69, 0.055)' : !ready(feature) ? 'rgba(255, 193, 7, 0.045)' : undefined,
                    borderLeft: core ? '4px solid #0d6efd' : undefined,
                  }}>
                    <td>
                      <div className="fw-semibold">{feature.nombre}</div>
                      <small className="text-secondary-light">{feature.id}</small>
                      {core && <div><span className="badge bg-info-50 text-info-600 mt-1">{feature.mandatory_core ? 'Core obligatoria' : 'Core plataforma'}</span></div>}
                    </td>
                    <td style={{ minWidth: 240 }}>{feature.descripcion || 'Sin descripción'}</td>
                    <td><div>{statusBadge(feature.discovery_state, 'discovered', { discovered: 'Descubierta', pending: 'Pendiente' })}{statusBadge(feature.validation_state, 'validated', { validated: 'Validada', rejected: 'Rechazada', pending: 'Pendiente' })}</div><div className="mt-1">{statusBadge(feature.migration_state, 'migrated', { migrated: 'Migrada', failed: 'Fallida', pending: 'Pendiente' })}{statusBadge(feature.installation_state, 'installed', { installed: 'Instalada', failed: 'Fallida', pending: 'Pendiente' })}</div></td>
                    <td><span className={`badge me-1 ${feature.manifest_valid ? 'bg-success-50 text-success-600' : 'bg-danger-50 text-danger-600'}`}>{feature.manifest_valid ? 'Manifiesto válido' : 'Manifiesto inválido'}</span><span className={`badge ${ready(feature) ? 'bg-success-50 text-success-600' : 'bg-warning-50 text-warning-700'}`} title={blockerLabel(feature)}>{ready(feature) ? 'Lista' : 'Bloqueada'}</span></td>
                    <td><span className={`badge ${feature.tenant_enabled ? 'bg-success-50 text-success-600' : 'bg-danger-50 text-danger-600'}`}>{feature.tenant_enabled ? 'Habilitada' : 'Deshabilitada'}</span></td>
                    <td className="text-end text-nowrap"><Link className="btn btn-sm btn-outline-secondary me-2" to={`/features/${encodeURIComponent(feature.id)}/roles`}>Roles</Link><button className="btn btn-sm btn-outline-primary me-2" onClick={() => setEditing({ id: feature.id, nombre: feature.nombre || '', descripcion: feature.descripcion || '' })}><Icon icon="solar:pen-2-linear" className="me-1" />Editar</button>{feature.mandatory_core ? <span className="text-secondary-light small">Siempre activa</span> : <button className={`btn btn-sm ${feature.tenant_enabled ? 'btn-outline-danger' : 'btn-success'}`} disabled={busy === feature.id || (!feature.tenant_enabled && !ready(feature))} title={!feature.tenant_enabled && !ready(feature) ? blockerLabel(feature) : ''} onClick={() => toggle(feature)}>{busy === feature.id ? 'Aplicando...' : feature.tenant_enabled ? 'Deshabilitar' : 'Habilitar'}</button>}</td>
                  </tr>
                </React.Fragment>;
              })}
              {filteredFeatures.length === 0 && <tr><td colSpan="6" className="text-center text-secondary-light py-32">No hay features que coincidan con la búsqueda.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
